import { css } from "@emotion/react";
import React, { createContext, useCallback, useContext, useMemo } from "react";
import { Portal } from "../Portal";
import { useTheme } from "../theme";
import { Alert, AlertMessageType, AlertOptions, AlertProps } from "./Alert";

type ThrowAlert = <E extends Error | Error[]>(
  error: E | undefined,
  options: {
    errorMessage?: AlertMessageType;
    successMessage?: AlertMessageType;
    ignoreSuccess?: boolean;
    onError?: (error: E) => void;
    onSuccess?: () => void;
  },
) => void;

interface AlertContextValue {
  addAlert: (message: AlertMessageType, options?: AlertOptions) => string;
  removeAlert: (id: string) => void;
  throwAlert: ThrowAlert;
}

const AlertContext = createContext({} as AlertContextValue);

export const alertPositions = [
  "top-left",
  "top-center",
  "top-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
] as const;

export type AlertPosition = (typeof alertPositions)[number];

const sx = {
  "bottom-right": {
    right: 0,
    bottom: 0,
    alignItems: "flex-end",
  },
  "bottom-center": {
    bottom: 0,
    left: 0,
    right: 0,
    margin: "auto",
    alignItems: "center",
  },
  "bottom-left": {
    left: 0,
    bottom: 0,
    alignItems: "flex-start",
  },
  "top-right": {
    right: 0,
    top: 0,
    alignItems: "flex-end",
  },
  "top-center": {
    top: 0,
    left: 0,
    right: 0,
    margin: "auto",
    alignItems: "center",
  },
  "top-left": {
    left: 0,
    top: 0,
    alignItems: "flex-start",
  },
};

export interface AlertProviderProps {
  position?: AlertPosition;
  children: React.ReactNode;
}

export const AlertProvider = ({
  children,
  position = "bottom-right",
}: AlertProviderProps) => {
  const [toasts, setAlerts] = React.useState<AlertProps[]>([]);

  const addAlert = useCallback(
    (message: AlertMessageType, options?: AlertOptions) => {
      const id = options?.id ?? Math.random().toString(36).substr(2, 9);

      setAlerts((pre) => {
        return [...pre, { id, message, ...options }];
      });
      return id;
    },
    [],
  );

  const removeAlert = useCallback((id: string) => {
    setAlerts((toasts) => toasts.filter((toast) => toast.id !== id));
  }, []);

  const throwAlert: ThrowAlert = useCallback(
    (error, options) => {
      const errorArr = error && [error].flat();
      if (errorArr && errorArr.length > 0) {
        const message =
          typeof options.errorMessage === "object"
            ? options.errorMessage
            : {
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                happend: options.errorMessage || "エラーが発生しました",
              };

        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        const log = errorArr.join(",");

        addAlert(
          {
            log,
            ...message,
          },
          {
            severity: "error",
          },
        );
        console.error(error);
        options.onError?.(error);
      } else {
        if (options.ignoreSuccess !== true) {
          addAlert(options.successMessage ?? "リクエストが成功しました", {
            severity: "success",
            duration: 3500,
          });
        }
        options.onSuccess?.();
      }
    },
    [addAlert],
  );

  const theme = useTheme();

  const contextValue = useMemo(() => {
    return { addAlert, removeAlert, throwAlert };
  }, [addAlert, removeAlert, throwAlert]);

  return (
    <AlertContext.Provider value={contextValue}>
      <Portal>
        <div
          css={[
            {
              zIndex: theme.zIndex.tooltip,
              padding: theme.spacing,
              gap: theme.spacing,
              ...sx[position],
            },
            css`
              position: fixed;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              width: fit-content;
              max-height: 100%;
              overflow: hidden;
            `,
          ]}
        >
          {toasts.map((toast) => (
            <Alert {...toast} key={toast.id} />
          ))}
        </div>
      </Portal>
      {children}
    </AlertContext.Provider>
  );
};

export const useAlert = (): AlertContextValue => {
  const ctx = useContext(AlertContext);
  return ctx;
};
